import React, { Fragment, useEffect, useState } from "react";
import {
  PlusCircleIcon,
  XMarkIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import DatePicker from "react-tailwindcss-datepicker";
import IconButton from "../../../iconButton";
import { alertSlideOverColors } from "../adminData";
import { useFormik } from "formik";
import * as Yup from "yup";

const AdminAlertSlideOver = ({
  data,
  show,
  setShowSlide,
  handleClose,
  type,
  loader,
  createAnnouncement,
  updateAnnouncement,
}) => {
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    content: "",
    start_at: "",
    end_at: "",
    active: false,
    color: "",
  });
  const [selectedBgColor, setSelectedBgColor] = useState("");

  useEffect(() => {
    if (data != null) {
      let tempNewAnnouncement = { ...newAnnouncement };
      tempNewAnnouncement.title = data.title;
      tempNewAnnouncement.content = data.content;
      tempNewAnnouncement.start_at = data.start_at;
      tempNewAnnouncement.end_at = data.end_at;
      tempNewAnnouncement.active = data.active;
      tempNewAnnouncement.color = data.color;
      tempNewAnnouncement.cta_text = data.cta_text;
      tempNewAnnouncement.cta_link = data.cta_link;
      setNewAnnouncement(tempNewAnnouncement);
      formik.setFieldValue("title", data.title);
      formik.setFieldValue("content", data.content);
      formik.setFieldValue("start_at", data.start_at);
      formik.setFieldValue("end_at", data.end_at);
      setSelectedBgColor(data.color);
      formik.setFieldValue("color", data.color);
      formik.setFieldValue("cta_text", data.cta_text);
      formik.setFieldValue("cta_link", data.cta_link);
    } else {
      setNewAnnouncement({
        title: "",
        content: "",
        start_at: "",
        end_at: "",
        active: false,
        color: "",
        cta_text: "",
        cta_link: "",
      });
    }
  }, [data]);

  const handleValueChange = (newValue, opt) => {
    let tempNewAnnouncement = { ...newAnnouncement };
    if (opt === "start_at") {
      tempNewAnnouncement.start_at = newValue.startDate;
    }
    if (opt === "end_at") {
      tempNewAnnouncement.end_at = newValue.endDate;
    }
    setNewAnnouncement(tempNewAnnouncement);
  };

  const handleInputChange = (newValue, opt) => {
    let tempNewAnnouncement = { ...newAnnouncement };
    tempNewAnnouncement[opt] = newValue;
    setNewAnnouncement(tempNewAnnouncement);
  };

  const formik = useFormik({
    initialValues: {
      title: data ? data.title : "",
      content: data ? data.content : "",
      start_at: data ? data.start_at : "",
      end_at: data ? data.end_at : "",
      cta_text: data ? data.cta_text : "",
      cta_link: data ? data.cta_link : "",
      color: data ? data.color : "",
    },
    validationSchema: Yup.object({
      title: Yup.string().required("Title is required."),
      content: Yup.string().required("Content is required."),
      start_at: Yup.string().required("Start Date is required."),
      end_at: Yup.string().required("End Date is required."),
      color: Yup.string().required("Color is required."),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    onSubmit: (values) => {
      const updatedValues = { ...values, _method: "PUT" };
      data
        ? updateAnnouncement({ id: data.id, ...updatedValues })
        : createAnnouncement(values);
      handleClose();
    },
  });

  return (
    <Transition.Root show={show} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden dashboard_calander_slideover">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full px-[15px] py-5 md:p-0">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-[478px]">
                  <div className="flex h-full flex-col bg-white py-5 md:pt-10 md:pb-5 px-4 md:px-8 shadow-xl rounded-md md:rounded-0">
                    <div className="border-b border-solid border-[#CCD0D4] pb-3 md:pb-[30px] mb-4 md:mb-6">
                      <div className="flex items-center justify-between mb-2 md:mb-5">
                        <Dialog.Title className="text-xl md:text-2xl font-bold leading-[160%] text-[#04091E]">
                          {type} {!data && "New"} Alert
                        </Dialog.Title>
                        <XMarkIcon
                          className="w-6 h-6 cursor-pointer text-black hover:text-red-700"
                          onClick={handleClose}
                        />
                      </div>
                      <p className="text-sm md:text-base font-normal leading-[160%] text-[#485868]">
                        This alert will display at the top of the website for
                        all Professional users. Confirm you have no spelling
                        errors before saving.
                      </p>
                    </div>
                    <form className="scroller max-h-[100vh] overflow-y-auto pr-1 mb-[30px] dark-field">
                      <div className="mb-[22px]">
                        <label className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3 block">
                          Headline
                        </label>
                        <input
                          name="title"
                          type="text"
                          value={newAnnouncement.title}
                          onChange={(e) => {
                            handleInputChange(e.target.value, "title");
                            formik.handleChange(e);
                          }}
                          placeholder="System Updates"
                          className="p-5 rounded-[10px] border border-strokeColor text-sm md:text-lg font-medium leading-[160%] text-customGray placeholder:text-[#A7AFB7] placeholder:font-medium h-12 md:h-[70px]"
                        />
                        {formik.touched.title && formik.errors.title ? (
                          <div className="text-red-500 text-sm font-semibold mt-1">
                            {formik.errors.title}
                          </div>
                        ) : null}
                      </div>
                      <div className="mb-[22px]">
                        <label className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3 block">
                          Body
                        </label>
                        <textarea
                          name="content"
                          value={newAnnouncement.content}
                          onChange={(e) => {
                            handleInputChange(e.target.value, "content");
                            formik.handleChange(e);
                          }}
                          placeholder="Body"
                          className="block w-full p-5 rounded-[10px] border border-strokeColor min-h-[120px] md:min-h-[127px] resize-none outline-none text-sm md:text-lg font-medium leading-[160%] text-customGray placeholder:text-[#A7AFB7]"
                        ></textarea>
                        {formik.touched.content && formik.errors.content ? (
                          <div className="text-red-500 font-semibold text-sm mt-1">
                            {formik.errors.content}
                          </div>
                        ) : null}
                      </div>

                      <div className="mb-[22px]">
                        <label className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3 block">
                          CTA Button (optional)
                        </label>
                        <input
                          type="text"
                          name="cta_text"
                          value={newAnnouncement.cta_text}
                          onChange={(e) => {
                            handleInputChange(e.target.value, "cta_text");
                            formik.handleChange(e);
                          }}
                          placeholder="CTA Button"
                          className="p-5 rounded-[10px] border border-strokeColor text-sm md:text-lg font-medium leading-[160%] text-customGray placeholder:text-[#A7AFB7] placeholder:font-bold h-12 md:h-[70px]"
                        />
                      </div>
                      <div className="mb-[22px]">
                        <label className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3 block">
                          CTA Link (optional)
                        </label>
                        <input
                          type="text"
                          name="cta_link"
                          value={newAnnouncement.cta_link}
                          onChange={(e) => {
                            handleInputChange(e.target.value, "cta_link");
                            formik.handleChange(e);
                          }}
                          placeholder="CTA Link"
                          className="p-5 rounded-[10px] border border-strokeColor text-sm md:text-lg font-medium leading-[160%] text-customGray placeholder:text-[#A7AFB7] placeholder:font-bold h-12 md:h-[70px]"
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-3 mb-[22px]">
                        <div>
                          <label className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3 block">
                            Start Date
                          </label>
                          <div className="alert-date-picker flex items-center p-5 rounded-[10px] border border-strokeColor bg-white h-12 md:h-[70px]">
                            <div className="w-9 pr-2.5 border-r border-strokeColor">
                              <CalendarDaysIcon className="w-6 h-6 text-customGray" />
                            </div>
                            <div className="max-w-[calc(100%-40px)] w-full">
                              <DatePicker
                                inputName={"start_at"}
                                primaryColor={"blue"}
                                toggleClassName="hidden"
                                useRange={false}
                                asSingle={true}
                                readOnly
                                value={{
                                  startDate: newAnnouncement.start_at,
                                  endDate: newAnnouncement.start_at,
                                }}
                                onChange={(values, e) => {
                                  handleValueChange(values, "start_at");
                                  formik.setFieldValue(
                                    "start_at",
                                    values.startDate
                                  );
                                }}
                                displayFormat={"MM/DD/YYYY"}
                              />
                            </div>
                          </div>
                          {formik.touched.start_at && formik.errors.start_at ? (
                            <div className="text-red-500 text-sm font-semibold mt-1">
                              {formik.errors.start_at}
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <label className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3 block">
                            End Date
                          </label>
                          <div className="alert-date-picker flex items-center p-5 rounded-[10px] border border-strokeColor bg-white h-12 md:h-[70px]">
                            <div className="w-9 pr-2.5 border-r border-strokeColor">
                              <CalendarDaysIcon className="w-6 h-6 text-customGray" />
                            </div>
                            <DatePicker
                              inputName="end_at"
                              primaryColor={"blue"}
                              toggleClassName="hidden"
                              useRange={false}
                              asSingle={true}
                              readOnly
                              value={{
                                startDate: newAnnouncement.end_at,
                                endDate: newAnnouncement.end_at,
                              }}
                              onChange={(values) => {
                                handleValueChange(values, "end_at");
                                formik.setFieldValue("end_at", values.endDate);
                              }}
                              displayFormat={"MM/DD/YYYY"}
                            />
                          </div>
                          {formik.touched.end_at && formik.errors.end_at ? (
                            <div className="text-red-500 text-sm font-semibold mt-1">
                              {formik.errors.end_at}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="">
                        <label className="text-sm md:text-lg font-bold leading-[160%] text-[#485868] mb-3 block">
                          Background Color
                        </label>
                        <div className="flex items-center gap-2.5">
                          {alertSlideOverColors?.map((color) => (
                            <div
                              className={`w-8 h-8 rounded-[50%]  border flex items-center justify-center cursor-pointer`}
                              style={{
                                backgroundColor: `${color.py_color}`,
                                borderColor: `${
                                  selectedBgColor === color.py_color
                                    ? color.sy_color
                                    : ""
                                }`,
                              }}
                              onClick={() => {
                                setSelectedBgColor(color.py_color);
                                formik.setFieldValue("color", color.py_color);
                              }}
                            >
                              <span
                                className={`block w-2 h-2 rounded-[50%]`}
                                style={{ backgroundColor: `${color.sy_color}` }}
                              ></span>
                            </div>
                          ))}
                        </div>
                        {formik.touched.color && formik.errors.color ? (
                          <div className="text-red-500 text-sm font-semibold mt-1">
                            {formik.errors.color}
                          </div>
                        ) : null}
                      </div>
                    </form>
                    <div className="grid grid-cols-1 gap-5">
                      <IconButton
                        title={data ? "Update" : "ADD NEW"}
                        icon={PlusCircleIcon}
                        classes={`mb-5 md:mb-0 flex items-center bg-primary text-sm md:text-base rounded-[10px] text-white font-black uppercase leading-[160%] h-10 md:h-[58px] w-full justify-center cursor-pointer`}
                        onClick={formik.handleSubmit}
                        iconClasses="icon w-6 h-6 -ml-0.5 mr-[10px]"
                        buttonLoader={loader}
                      />
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AdminAlertSlideOver;
